/**
 * Common date and time utility functions for PPA components
 */

import {
  addYears,
  getDaysInMonth as dateFnsGetDaysInMonth,
  endOfYear,
  format,
} from "date-fns";

/**
 * Get the localized name of a month
 *
 * @throws {Error} When monthNumber is not between 1-12
 */
export function getMonthName(monthNumber: number): string {
  if (monthNumber < 1 || monthNumber > 12) {
    throw new Error("Month number must be between 1 and 12");
  }

  // Create a date with the given month (year doesn't matter for month name)
  const date = new Date(2000, monthNumber - 1, 1);
  return format(date, "MMMM");
}

/**
 * Get the number of days in a given month for a specific year
 *
 * @throws {Error} When month is not between 1-12
 */
export function getDaysInMonth(
  month: number,
  year: number = new Date().getFullYear()
): number {
  if (month < 1 || month > 12) {
    throw new Error("Month must be between 1 and 12");
  }

  // Create date with the given month and year (day 1 is fine for getting days in month)
  const date = new Date(year, month - 1, 1);
  return dateFnsGetDaysInMonth(date);
}

/**
 * Create a date spec with today's date
 */
export function createTodayDateSpec(): { day: number; month: number } {
  const today = new Date();
  return {
    day: today.getDate(),
    month: today.getMonth() + 1, // getMonth() returns 0-11, we need 1-12
  };
}

/**
 * Get next year's date string for default end dates
 */
export function getNextYearEndDate(): string {
  const nextYear = addYears(new Date(), 1);
  const endOfNextYear = endOfYear(nextYear);
  return format(endOfNextYear, "yyyy-MM-dd");
}
